import { List, Spin, Tag } from 'antd'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { fetchNotifications } from 'src/api/notification'
import './Notification.css'

interface NotificationData {
  username: string
  postId: string
  commentId?: string
  replyCommentId?: string
  fromUser: string
  imageS3Path?: string
  type:
    | 'SOCIAL_COMMENT_LIKE'
    | 'SOCIAL_POST_COMMENT'
    | 'SOCIAL_POST_REPLY'
    | 'SOCIAL_POST_MENTION'
    | 'SOCIAL_POST_LIKE'
    | 'SOCIAL_FOLLOW'
    | 'SOCIAL_POST_SHARE'
}

interface NotificationItem {
  id: string
  notificationType:
    | 'SOCIAL_COMMENT_LIKE'
    | 'SOCIAL_POST_COMMENT'
    | 'SOCIAL_POST_REPLY'
    | 'SOCIAL_POST_MENTION'
    | 'SOCIAL_POST_LIKE'
    | 'SOCIAL_FOLLOW'
    | 'SOCIAL_POST_SHARE'
  title: string
  message: string
  unRead: boolean
  createdOn: string
  data: NotificationData
}
interface NotificationResponse {
  data: NotificationItem[]
  total_items: number
}
interface NotificationProps {
  botUserId: string
}

const NOTIFICATION_TYPE_MAP: Record<string, { label: string; color: string }> =
  {
    SOCIAL_COMMENT_LIKE: { label: 'Like', color: 'green' },
    SOCIAL_POST_COMMENT: { label: 'Comment', color: 'blue' },
    SOCIAL_POST_REPLY: { label: 'Reply', color: 'yellow' },
    SOCIAL_POST_MENTION: { label: 'Mention', color: 'red' },
    SOCIAL_POST_LIKE: { label: 'Like', color: 'green' },
    SOCIAL_FOLLOW: { label: 'Follow', color: 'red' },
    SOCIAL_POST_SHARE: { label: 'Share', color: 'indigo' },
  }

const Notification: React.FC<NotificationProps> = ({ botUserId }) => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [page, setPage] = useState<number>(0)
  const [pageSize] = useState<number>(5)
  const [totalItems, setTotalItems] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const observer = useRef<IntersectionObserver | null>(null)

  const handleClickOpen = (record: NotificationItem) => {
    const params = new URLSearchParams()
    params.append('botUserId', botUserId)
    params.append('text', record.message)
    navigate(
      `/social/bot-post/comments/${record.data.postId}?${params.toString()}`
    )
  }
  const lastNotificationRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setPage((prev) => prev + 1)
          }
        },
        { threshold: 0.1 }
      )
      if (node) observer.current.observe(node)
    },
    [loading, hasMore]
  )

  useEffect(() => {
    const fetchData = async () => {
      if (!hasMore) return
      setLoading(true)
      try {
        const response: NotificationResponse = await fetchNotifications(
          page,
          pageSize,
          botUserId
        )
        setNotifications((prev) => [...prev, ...response.data])
        setTotalItems(response.total_items)
        setHasMore(
          response.data.length === pageSize &&
            notifications.length + response.data.length < response.total_items
        )
      } catch (error) {
        console.error('Error fetching notifications:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [page, botUserId, pageSize])

  useEffect(() => {
    setNotifications([])
    setPage(0)
    setHasMore(true)
  }, [botUserId])

  return (
    <div style={{ maxWidth: 800, margin: 'auto', padding: 24 }}>
      <div className="notification-container">
        <List
          itemLayout="horizontal"
          loading={false}
          dataSource={notifications}
          renderItem={(item: NotificationItem, index: number) => {
            const typeTag = NOTIFICATION_TYPE_MAP[item.notificationType]
            return (
              <List.Item
                ref={
                  index === notifications.length - 1
                    ? lastNotificationRef
                    : null
                }
                className="notification-item"
                onClick={() => handleClickOpen(item)}
                style={{
                  backgroundColor: item.unRead ? '#a4f5f1' : '#f1d0f5',
                  borderRadius: 8,
                  marginBottom: 8,
                  padding: 16,
                  color: 'black',
                }}
              >
                <List.Item.Meta
                  title={
                    <span style={{ color: 'black' }}>
                      <b style={{ color: 'black' }}>{item.data.username}</b> —{' '}
                      {item.title}
                      {typeTag && (
                        <Tag
                          color={typeTag.color}
                          style={{ marginLeft: 8, fontWeight: 500 }}
                        >
                          {typeTag.label}
                        </Tag>
                      )}
                    </span>
                  }
                  description={
                    <>
                      <div>{item.message}</div>
                      <div style={{ fontSize: 12, color: '#999' }}>
                        {dayjs(item.createdOn).format('DD MMM YYYY, HH:mm')}
                      </div>
                    </>
                  }
                />
              </List.Item>
            )
          }}
        />
        {loading && (
          <div className="loading-spinner" aria-live="polite">
            <Spin />
          </div>
        )}
        {!hasMore && notifications.length > 0 && (
          <div className="end-message" aria-live="polite">
            No more notifications
          </div>
        )}
      </div>
    </div>
  )
}

export default Notification
